import { useState, useCallback, useEffect } from 'react'

export interface MicrointeractionConfig {
  duration?: number
  delay?: number
  easing?: 'ease' | 'ease-in' | 'ease-out' | 'ease-in-out' | 'linear'
}

export interface HoverState {
  isHovered: boolean
  isPressed: boolean
}

export interface FocusState {
  isFocused: boolean
  isActive: boolean
}

export const useMicrointeractions = (config: MicrointeractionConfig = {}) => {
  const {
    duration = 200,
    delay = 0,
    easing = 'ease-out'
  } = config

  // Estados de hover
  const [hoverState, setHoverState] = useState<HoverState>({
    isHovered: false,
    isPressed: false
  })

  // Estados de focus
  const [focusState, setFocusState] = useState<FocusState>({
    isFocused: false,
    isActive: false
  })

  // Handlers para hover
  const handleMouseEnter = useCallback(() => {
    setHoverState(prev => ({ ...prev, isHovered: true }))
  }, [])

  const handleMouseLeave = useCallback(() => {
    setHoverState(prev => ({ ...prev, isHovered: false, isPressed: false }))
  }, [])

  const handleMouseDown = useCallback(() => {
    setHoverState(prev => ({ ...prev, isPressed: true }))
  }, [])

  const handleMouseUp = useCallback(() => {
    setHoverState(prev => ({ ...prev, isPressed: false }))
  }, [])

  // Handlers para focus
  const handleFocus = useCallback(() => {
    setFocusState(prev => ({ ...prev, isFocused: true }))
  }, [])

  const handleBlur = useCallback(() => {
    setFocusState(prev => ({ ...prev, isFocused: false, isActive: false }))
  }, [])

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      setFocusState(prev => ({ ...prev, isActive: true }))
    }
  }, [])

  const handleKeyUp = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      setFocusState(prev => ({ ...prev, isActive: false }))
    }
  }, [])

  // Classes CSS dinâmicas
  const getHoverClasses = useCallback(() => {
    const classes = []
    
    if (hoverState.isHovered) {
      classes.push('hover:scale-105', 'hover:shadow-medium')
    }
    
    if (hoverState.isPressed) {
      classes.push('scale-95', 'shadow-inner-soft')
    }
    
    return classes.join(' ')
  }, [hoverState])

  const getFocusClasses = useCallback(() => {
    const classes = []
    
    if (focusState.isFocused) {
      classes.push('ring-2', 'ring-tamoaki-primary', 'ring-offset-2')
    }
    
    if (focusState.isActive) {
      classes.push('scale-95')
    }
    
    return classes.join(' ')
  }, [focusState])

  // Estilos CSS dinâmicos
  const getTransitionStyles = useCallback(() => ({
    transition: `all ${duration}ms ${easing} ${delay}ms`,
    transform: hoverState.isPressed ? 'scale(0.95)' : hoverState.isHovered ? 'scale(1.02)' : 'scale(1)',
    boxShadow: hoverState.isPressed 
      ? 'inset 0 2px 4px rgba(0, 0, 0, 0.1)' 
      : hoverState.isHovered 
        ? '0 4px 16px rgba(0, 0, 0, 0.15)' 
        : '0 2px 8px rgba(0, 0, 0, 0.08)'
  }), [duration, easing, delay, hoverState])

  return {
    // Estados
    hoverState,
    focusState,
    
    // Handlers
    handleMouseEnter,
    handleMouseLeave,
    handleMouseDown,
    handleMouseUp,
    handleFocus,
    handleBlur,
    handleKeyDown,
    handleKeyUp,
    
    // Classes e estilos
    getHoverClasses,
    getFocusClasses,
    getTransitionStyles,
    
    // Props combinados para elementos
    hoverProps: {
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      onMouseDown: handleMouseDown,
      onMouseUp: handleMouseUp
    },
    
    focusProps: {
      onFocus: handleFocus,
      onBlur: handleBlur,
      onKeyDown: handleKeyDown,
      onKeyUp: handleKeyUp
    },
    
    interactionProps: {
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      onMouseDown: handleMouseDown,
      onMouseUp: handleMouseUp,
      onFocus: handleFocus,
      onBlur: handleBlur,
      onKeyDown: handleKeyDown,
      onKeyUp: handleKeyUp
    }
  }
}

// Hook para animações de entrada
export const useEntranceAnimation = (delay: number = 0) => {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true)
    }, delay)

    return () => clearTimeout(timer)
  }, [delay])

  const getEntranceClasses = useCallback(() => {
    return isVisible 
      ? 'animate-fade-in opacity-100 translate-y-0' 
      : 'opacity-0 translate-y-4'
  }, [isVisible])

  const getEntranceStyles = useCallback(() => ({
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
    transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
  }), [isVisible])

  return {
    isVisible,
    getEntranceClasses,
    getEntranceStyles
  }
}

// Hook para feedback tátil
export const useHapticFeedback = () => {
  const triggerHaptic = useCallback((type: 'light' | 'medium' | 'heavy' = 'light') => {
    if ('vibrate' in navigator) {
      const patterns = {
        light: 10,
        medium: 50,
        heavy: 100
      }
      navigator.vibrate(patterns[type])
    }
  }, [])

  const triggerSuccess = useCallback(() => {
    triggerHaptic('light')
  }, [triggerHaptic])

  const triggerError = useCallback(() => {
    triggerHaptic('medium')
  }, [triggerHaptic])

  const triggerWarning = useCallback(() => {
    triggerHaptic('light')
  }, [triggerHaptic])

  return {
    triggerHaptic,
    triggerSuccess,
    triggerError,
    triggerWarning
  }
}

// Hook para animações de loading
export const useLoadingAnimation = (isLoading: boolean = false) => {
  const [pulseCount, setPulseCount] = useState(0)

  useEffect(() => {
    if (!isLoading) return

    const interval = setInterval(() => {
      setPulseCount(prev => prev + 1)
    }, 1000)

    return () => clearInterval(interval)
  }, [isLoading])

  const getLoadingClasses = useCallback(() => {
    if (!isLoading) return ''
    
    return 'animate-pulse opacity-75'
  }, [isLoading])

  const getSpinnerClasses = useCallback(() => {
    return isLoading ? 'animate-spin' : ''
  }, [isLoading])

  return {
    pulseCount,
    getLoadingClasses,
    getSpinnerClasses
  }
} 